import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import Login from "./pages/Login";
import SchoolDemoPage from "./pages/SchoolDemoPage";
import ResetLinkSent from "./pages/ResetLinkSent.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import StudentOrganizationViewer from "./components/StudentOrganizationViewer";
import StudentProfile from "./pages/StudentProfile";
// REMOVED: GenerateCurriculumPageNew - deleted in teaching plan reset
import PrivateRoute from "./components/PrivateRoute";
import PendingApprovalGate from "./components/PendingApprovalGate.jsx";
// REMOVED: GenerateTermPlanPage - deleted in teaching plan reset
import TestPage from "./pages/TestPage";
import InterventionManagementPage from "./pages/InterventionManagementPage";
import InterventionTestPage from "./pages/InterventionTestPage";
import InterventionGroupsPage from "./pages/InterventionGroupsPage";
import ProgressDashboardPage from "./pages/ProgressDashboardPage";
import SencoCohortDataPage from "./pages/SencoCohortDataPage";
import ProvisionMapsPage from "./pages/ProvisionMapsPage.jsx";
import EHCNAIndexPage from "./features/ehcna/EHCNAIndexPage.jsx";
import EHCNAWizardPage from "./features/ehcna/EHCNAWizardPage.jsx";
import TestDataViewer from "./components/TestDataViewer";
import TeachingPlan from "./pages/TeachingPlan.jsx";
import TeachingPlanQuestionnairePage from "./pages/TeachingPlanQuestionnairePage";
import OnePageProfilePage from "./pages/OnePageProfilePage.jsx";
import OnePageProfilesPage from "./pages/OnePageProfilesPage.jsx";
import IndividualSupportPlansPage from "./pages/IndividualSupportPlansPage";
// REMOVED: TeachingPlansListPage - deleted in teaching plan reset
// REMOVED: GroupTeachingPlanCreate - deleted in teaching plan reset
import GroupQuestionnaireFlow from "./pages/GroupQuestionnaireFlow";
// REMOVED: GroupTeachingPlanPage - deleted in teaching plan reset
import StudentLoadingDiagnostic from "./components/StudentLoadingDiagnostic";
import StartPlanPage from "./pages/StartPlanPage";
import TriagePage from "./pages/TriagePage";
import DecisionPage from "./pages/DecisionPage";
import InterventionsBuilderPage from "./pages/InterventionsBuilderPage";
import StaffDirectory from "./pages/StaffDirectory";
import StaffProfile from "./pages/StaffProfile.jsx";
import SchoolSettingsPage from "./pages/SchoolSettingsPage";
import AdminUserApprovals from "./pages/AdminUserApprovals.jsx";
import AdminActivityEditor from "./pages/AdminActivityEditor.jsx";
import AdminResourcesDashboard from "./pages/AdminResourcesDashboard.jsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.jsx";
import TermsOfService from "./pages/TermsOfService.jsx";
import SecurityPage from "./pages/SecurityPage.jsx";
import CookiesPolicy from "./pages/CookiesPolicy.jsx";
import AboutPage from "./pages/AboutPage.jsx";
import ContactPage from "./pages/ContactPage.jsx";
import CookieConsent from "./components/CookieConsent.jsx";
import StudentInterventionHistory from "./pages/StudentInterventionHistory.jsx";
// REMOVED: SecondaryPathView - deleted in teaching plan reset
import InterventionsOnlyView from "./pages/InterventionsOnlyView.jsx";
import ResourceLibraryPage from "./pages/ResourceLibraryPage.jsx";
// Staff Directory visible to all roles; rates hidden in UI by role

// Help page imports
import {
  HelpHubPage,
  HelpDashboardPage,
  HelpStudentManagementPage,
  HelpCurriculumGenerationPage,
  HelpProgressTrackingPage,
  HelpTermPlanningPage,
  HelpGraduatedResponsePage
} from "./help";

// Solo user pages
import SoloSignupPage from "./pages/SoloSignupPage";
import SoloCheckoutPage from "./pages/solo/SoloCheckoutPage";
import SubscriptionSuccessPage from "./pages/solo/SubscriptionSuccessPage";
import SoloDashboard from "./pages/solo/SoloDashboard";
import SoloSearch from "./pages/solo/SoloSearch";
import SoloActivityDetail from "./pages/solo/SoloActivityDetail";
import SoloAccountPage from "./pages/solo/SoloAccountPage";
import SymbolGenerator from "./pages/solo/SymbolGenerator";
import ResourceBuilder from "./pages/solo/ResourceBuilder";
import SensiPage from "./pages/solo/SensiPage";
import LearningProfilesList from "./pages/solo/LearningProfilesList";
import LearningProfileBuilder from "./pages/solo/LearningProfileBuilder";
import LearningProfileResults from "./pages/solo/LearningProfileResults";
import SoloTimetableBuilder from "./pages/solo/SoloTimetableBuilder";
import ArticlesPage from "./pages/solo/ArticlesPage";
import ArticleDetail from "./pages/solo/ArticleDetail";
import ResourcesPage from "./pages/solo/ResourcePacksPage";

// Assessments & Screeners - School (TODO: Create these files)
// import AssessmentsPage from "./pages/AssessmentsPage";
// import AssessmentRunner from "./pages/AssessmentRunner";
// import AssessmentResults from "./pages/AssessmentResults";
// import BlankAssessmentDownload from "./pages/BlankAssessmentDownload";

// Assessments & Screeners - Solo
import SoloAssessmentsPage from "./pages/solo/SoloAssessmentsPage";
// import SoloAssessmentRunner from "./pages/solo/SoloAssessmentRunner";
// import SoloAssessmentResults from "./pages/solo/SoloAssessmentResults";

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import DataProvider from './lib/data/DataProvider.jsx';
import AppLayout from './components/AppLayout.jsx';
import { auth, db } from './firebase';

const isSoloLanding = process.env.REACT_APP_SOLO_LANDING === 'true';

function SchoolRoute({ children, withLayout = true }) {
  const content = withLayout ? <AppLayout>{children}</AppLayout> : children;
  return (
    <PrivateRoute>
      <PendingApprovalGate>{content}</PendingApprovalGate>
    </PrivateRoute>
  );
}

/**
 * Smart redirect component that checks user type and redirects appropriately
 * Prevents solo users from ending up on school pages and vice versa.
 * Solo build: unauthenticated users go to / (chooser). Schools build: go to /login.
 */
function SmartRedirect() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkUserAndRedirect = async () => {
      const user = auth.currentUser;

      if (!user) {
        navigate(isSoloLanding ? '/' : '/login', { replace: true });
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));

        if (userDoc.exists()) {
          const userData = userDoc.data();

          if (userData.userType === 'solo' || userData.role === 'solo_user') {
            navigate('/solo/dashboard', { replace: true });
            return;
          }
        }

        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Error checking user type:', error);
        navigate(isSoloLanding ? '/' : '/login', { replace: true });
      } finally {
        setChecking(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        checkUserAndRedirect();
      } else {
        navigate(isSoloLanding ? '/' : '/login', { replace: true });
        setChecking(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500/20 border-t-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Redirecting...</p>
        </div>
      </div>
    );
  }

  return null;
}

function App() {
  return (
    <>
      <Toaster position="top-right" />
      <DataProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          {/* Cookie Consent Banner - Displays globally */}
          <CookieConsent />
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/demo" element={<SchoolDemoPage />} />
            <Route path="/reset-sent" element={<ResetLinkSent />} />
            <Route
              path="/test"
              element={
                <PrivateRoute>
                  <TestPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={<SchoolRoute><Dashboard /></SchoolRoute>}
            />
            <Route
              path="/students"
              element={<Navigate to="/student-organization" replace />}
            />
            <Route
              path="/student/:id"
              element={<SchoolRoute><StudentProfile /></SchoolRoute>}
            />
            <Route
              path="/students/:id/teaching-plan"
              element={<SchoolRoute><TeachingPlan /></SchoolRoute>}
            />
            <Route
              path="/students/:id/teaching-plan-questionnaire"
              element={<SchoolRoute><TeachingPlanQuestionnairePage /></SchoolRoute>}
            />
            {/* REMOVED: /teaching-plans - TeachingPlansListPage deleted */}
            <Route
              path="/students/:id/one-page-profile"
              element={<SchoolRoute><OnePageProfilePage /></SchoolRoute>}
            />
            <Route
              path="/one-page-profiles"
              element={<SchoolRoute><OnePageProfilesPage /></SchoolRoute>}
            />
            <Route
              path="/teaching-plans"
              element={<Navigate to="/one-page-profiles" replace />}
            />
            <Route
              path="/isps"
              element={<SchoolRoute><IndividualSupportPlansPage /></SchoolRoute>}
            />
            {/* REMOVED: /teaching-plans/:id - TeachingPlan deleted */}
            {/* REMOVED: /teaching-plans/group/create - GroupTeachingPlanCreate deleted */}
            <Route
              path="/teaching-plans/group/:groupId/questionnaire"
              element={<SchoolRoute><GroupQuestionnaireFlow /></SchoolRoute>}
            />
            {/* REMOVED: /teaching-plans/group/:groupId - GroupTeachingPlanPage deleted */}
            {/* REMOVED: /students/:id/teaching-plan - TeachingPlan deleted */}
            {/* REMOVED: /students/:id/teaching-plan-questionnaire - TeachingPlanQuestionnairePage deleted */}
            <Route
              path="/provision-maps"
              element={<SchoolRoute><ProvisionMapsPage /></SchoolRoute>}
            />
            <Route
              path="/staff"
              element={<SchoolRoute><StaffDirectory /></SchoolRoute>}
            />
            <Route
              path="/settings"
              element={<SchoolRoute><SchoolSettingsPage /></SchoolRoute>}
            />
            <Route
              path="/profile"
              element={<SchoolRoute><StaffProfile /></SchoolRoute>}
            />
            <Route
              path="/admin/user-approvals"
              element={<SchoolRoute><AdminUserApprovals /></SchoolRoute>}
            />
            <Route
              path="/admin/activities/:slug/edit"
              element={<SchoolRoute><AdminActivityEditor /></SchoolRoute>}
            />
            <Route
              path="/admin/resources"
              element={<SchoolRoute><AdminResourcesDashboard /></SchoolRoute>}
            />
            <Route
              path="/students/:id/start-plan"
              element={<SchoolRoute><StartPlanPage /></SchoolRoute>}
            />
            <Route
              path="/students/:id/triage"
              element={<SchoolRoute><TriagePage /></SchoolRoute>}
            />
            <Route
              path="/students/:id/decision"
              element={<SchoolRoute><DecisionPage /></SchoolRoute>}
            />
            <Route
              path="/students/:id/interventions"
              element={<SchoolRoute><InterventionsBuilderPage /></SchoolRoute>}
            />
            <Route
              path="/student/:id/interventions-only"
              element={<SchoolRoute><InterventionsOnlyView /></SchoolRoute>}
            />
            {/* REMOVED: /student/:id/secondary-path - SecondaryPathView deleted */}
            <Route
              path="/student/:id/interventions/history"
              element={<SchoolRoute><StudentInterventionHistory /></SchoolRoute>}
            />
            {/* REMOVED: /generate-curriculum - GenerateCurriculumPageNew deleted */}
            <Route
              path="/test-simple"
              element={
                <div>
                  <h1>Simple Test Page</h1>
                  <p>If you can see this, React is working!</p>
                  <p>Check the console for logs.</p>
                </div>
              }
            />
            {/* REMOVED: /generate-term-plan - GenerateTermPlanPage deleted */}
            <Route
              path="/ehcna"
              element={<SchoolRoute><EHCNAIndexPage /></SchoolRoute>}
            />
            <Route
              path="/ehcna/new"
              element={<SchoolRoute><EHCNAWizardPage /></SchoolRoute>}
            />
            <Route
              path="/ehcna/:requestId"
              element={<SchoolRoute><EHCNAWizardPage /></SchoolRoute>}
            />
            <Route
              path="/interventions"
              element={<SchoolRoute><InterventionManagementPage /></SchoolRoute>}
            />
            <Route
              path="/resources"
              element={<SchoolRoute><ResourceLibraryPage /></SchoolRoute>}
            />

            {/* Assessments & Screeners - School (TODO: Uncomment when files are created)
            <Route
              path="/assessments"
              element={
                <PrivateRoute>
                  <PendingApprovalGate>
                    <AssessmentsPage />
                  </PendingApprovalGate>
                </PrivateRoute>
              }
            />
            <Route
              path="/assessments/:templateId/run"
              element={
                <PrivateRoute>
                  <PendingApprovalGate>
                    <AssessmentRunner />
                  </PendingApprovalGate>
                </PrivateRoute>
              }
            />
            <Route
              path="/assessments/:templateId/blank"
              element={
                <PrivateRoute>
                  <PendingApprovalGate>
                    <BlankAssessmentDownload />
                  </PendingApprovalGate>
                </PrivateRoute>
              }
            />
            <Route
              path="/assessments/results/:resultId"
              element={
                <PrivateRoute>
                  <PendingApprovalGate>
                    <AssessmentResults />
                  </PendingApprovalGate>
                </PrivateRoute>
              }
            />
            */}

            {/* Advice Hub - Articles for School Portal */}
            <Route
              path="/articles"
              element={<SchoolRoute><ArticlesPage /></SchoolRoute>}
            />
            <Route
              path="/articles/:slug"
              element={<SchoolRoute><ArticleDetail /></SchoolRoute>}
            />
            <Route
              path="/intervention-groups"
              element={<SchoolRoute><InterventionGroupsPage /></SchoolRoute>}
            />
            <Route
              path="/intervention-demo"
              element={<SchoolRoute><InterventionTestPage /></SchoolRoute>}
            />

            <Route
              path="/progress-dashboard"
              element={<SchoolRoute><ProgressDashboardPage /></SchoolRoute>}
            />
            <Route
              path="/cohort-data"
              element={<SchoolRoute><SencoCohortDataPage /></SchoolRoute>}
            />

            {/* Redirect old graduated-response-report route to EHCNA page */}
            <Route
              path="/graduated-response-report"
              element={<Navigate to="/ehcna" replace />}
            />

            <Route
              path="/test-data"
              element={<SchoolRoute><TestDataViewer /></SchoolRoute>}
            />

            <Route
              path="/diagnostic"
              element={<SchoolRoute><StudentLoadingDiagnostic /></SchoolRoute>}
            />

            <Route
              path="/student-organization"
              element={<SchoolRoute><StudentOrganizationViewer /></SchoolRoute>}
            />

            {/* Help Routes */}
            <Route
              path="/help"
              element={<SchoolRoute><HelpHubPage /></SchoolRoute>}
            />
            <Route
              path="/help/dashboard"
              element={<SchoolRoute><HelpDashboardPage /></SchoolRoute>}
            />
            <Route
              path="/help/student-management"
              element={<SchoolRoute><HelpStudentManagementPage /></SchoolRoute>}
            />
            <Route
              path="/help/curriculum-generation"
              element={<SchoolRoute><HelpCurriculumGenerationPage /></SchoolRoute>}
            />
            <Route
              path="/help/progress-tracking"
              element={<SchoolRoute><HelpProgressTrackingPage /></SchoolRoute>}
            />
            <Route
              path="/help/term-planning"
              element={<SchoolRoute><HelpTermPlanningPage /></SchoolRoute>}
            />
            <Route
              path="/help/graduated-response"
              element={<SchoolRoute><HelpGraduatedResponsePage /></SchoolRoute>}
            />
            {/* Legal/Trust Pages - Public Routes */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/cookies" element={<CookiesPolicy />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Redirect old/incorrect intervention routes */}
            <Route path="/intervention-setup" element={<Navigate to="/interventions" replace />} />
            <Route path="/intervention-setup/*" element={<Navigate to="/interventions" replace />} />

            {/* Solo User Routes - Individual teacher subscription */}
            <Route path="/signup/solo" element={<SoloSignupPage />} />
            <Route path="/signup/beta" element={<SoloSignupPage isBeta={true} />} />
            <Route path="/solo" element={<Navigate to="/solo/dashboard" replace />} />
            <Route path="/solo/checkout" element={<SoloCheckoutPage />} />
            <Route path="/solo/subscription-success" element={<SubscriptionSuccessPage />} />
            <Route path="/solo/dashboard" element={<SoloDashboard />} />
            <Route path="/solo/search" element={<SoloSearch />} />
            <Route path="/solo/activity/:activityId" element={<SoloActivityDetail />} />
            <Route path="/solo/account" element={<SoloAccountPage />} />
            <Route path="/solo/symbols" element={<SymbolGenerator />} />
            <Route path="/solo/resources" element={<ResourceBuilder />} />
            <Route path="/solo/sensi" element={<SensiPage />} />

            {/* Advice Hub - Articles */}
            <Route path="/solo/articles" element={<ArticlesPage />} />
            <Route path="/solo/articles/:slug" element={<ArticleDetail />} />

            {/* Assessments & Screeners - Solo */}
            <Route path="/solo/assessments" element={<SoloAssessmentsPage />} />
            {/* Future routes when runner/results components are created:
            <Route path="/solo/assessments/:templateId/run" element={<SoloAssessmentRunner />} />
            <Route path="/solo/assessments/:templateId/blank" element={<BlankAssessmentDownload />} />
            <Route path="/solo/assessments/results/:resultId" element={<SoloAssessmentResults />} />
            */}

            {/* Learning Profile Builder */}
            <Route path="/solo/profiles" element={<LearningProfilesList />} />
            <Route path="/solo/profiles/new" element={<LearningProfileBuilder />} />
            <Route path="/solo/profiles/results" element={<LearningProfileResults />} />
            <Route path="/solo/profiles/:profileId" element={<LearningProfileResults />} />
            <Route path="/solo/profiles/:profileId/edit" element={<LearningProfileBuilder />} />

            {/* Timetable Builder */}
            <Route path="/solo/timetable" element={<SoloTimetableBuilder />} />
            <Route path="/solo/timetable/new" element={<SoloTimetableBuilder />} />
            <Route path="/solo/timetable/:profileId" element={<SoloTimetableBuilder />} />

            {/* Resources - Resource Packs and KB Items */}
            <Route path="/solo/resources" element={<ResourcesPage />} />
            {/* Legacy route redirect */}
            <Route path="/solo/packs" element={<Navigate to="/solo/resources" replace />} />

            {/* Catch-all route for unmatched paths - redirects based on user type */}
            <Route path="*" element={<SmartRedirect />} />
          </Routes>
        </Router>
      </DataProvider>
    </>
  );
}

export default App;
